import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import storageService from '../../../services/storageService';
import { updateAccountDetails } from '../../../services/userService';

export const useClientSettings = () => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();

  const isLegacyAdmin = false;

  const [enableExternalClientMode, setEnableExternalClientMode] = useState(
    account?.clientSettings?.enableExternalClientMode ?? false
  );

  const [hideBillingFromUsers, setHideBillingFromUsers] = useState(
    account?.clientSettings?.hideBillingFromUsers ?? true
  );

  const [generateCapabilityPitchDeck, setGenerateCapabilityPitchDeck] = useState(
    account?.clientSettings?.generateCapabilityPitchDeck ?? false
  );

  const [capabilityPitchTenderContextSlideLength, setCapabilityPitchTenderContextSlideLength] =
    useState(account?.clientSettings?.capabilityPitchTenderContextSlideLength ?? 5);

  const [capabilityPitchPitchSlideLength, setCapabilityPitchPitchSlideLength] = useState(
    account?.clientSettings?.capabilityPitchPitchSlideLength ?? 5
  );

  const [capabilityPitchOutputTypes, setCapabilityPitchOutputTypes] = useState(
    account?.clientSettings?.capabilityPitchOutputTypes ?? { pptx: true, html: false }
  );

  const [presentationEngine, setPresentationEngine] = useState<
    'marp' | 'python-pptx' | 'ppt-master' | 'pure-html'
  >(
    (account?.clientSettings?.presentationEngine as
      | 'marp'
      | 'python-pptx'
      | 'ppt-master'
      | 'pure-html') ?? 'marp'
  );

  const [enableProcurementModule, setEnableProcurementModule] = useState<boolean>(
    Boolean(account?.clientSettings?.enableProcurementModule)
  );

  const [enableBiddingModule, setEnableBiddingModule] = useState<boolean>(
    account?.clientSettings?.enableBiddingModule !== false
  );

  const [enablePartnerSearch, setEnablePartnerSearch] = useState(
    account?.enablePartnerSearch ?? false
  );

  const [matchmakingCountryFilter, setMatchmakingCountryFilter] = useState(
    account?.matchmakingCountryFilter ?? 'Australia'
  );

  const [matchmakingRegionFilter, setMatchmakingRegionFilter] = useState(
    account?.matchmakingRegionFilter ?? ''
  );

  const [matchmakingMinScore, setMatchmakingMinScore] = useState(
    account?.matchmakingMinScore ?? 0.7
  );

  const [enablePartnerVisibility, setEnablePartnerVisibility] = useState(
    account?.enablePartnerVisibility ?? false
  );

  const defaultAiSettings = {
    includeStrengths: true,
    includeLimitations: true,
    includeStrategy: true,
    includeScales: true,
    includeCriteria: true,
  };

  const [aiSettings, setAiSettings] = useState({
    ...defaultAiSettings,
    ...account?.aiSettings,
  });

  const [primaryColor, setPrimaryColor] = useState(account?.brand_identity?.primary_color ?? '');
  const [secondaryColor, setSecondaryColor] = useState(
    account?.brand_identity?.secondary_color ?? ''
  );
  const [fontFamily, setFontFamily] = useState(account?.brand_identity?.font_family ?? '');
  const [logoStoragePath, setLogoStoragePath] = useState(
    account?.brand_identity?.logo_storage_path ?? ''
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadLogoUrl = async () => {
      if (logoStoragePath) {
        try {
          const result = await storageService.getDownloadURL(logoStoragePath);
          if (result.success && isMounted) {
            setLogoUrl(result.url);
          }
        } catch (_error) {
          // ignore error
        }
      } else if (isMounted) {
        setLogoUrl(null);
      }
    };
    loadLogoUrl();
    return () => {
      isMounted = false;
    };
  }, [logoStoragePath]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !account?.clientId) {
      return;
    }

    setIsUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const storagePath = `clients/${account.clientId}/brand_assets/logo.${ext}`;
      const uploadResult = await storageService.uploadFile(storagePath, file);
      if (uploadResult.success) {
        setLogoStoragePath(uploadResult.path);
        const result = await storageService.getDownloadURL(uploadResult.path);
        if (result.success) {
          const cacheBuster = `&t=${Date.now()}`;
          setLogoUrl(result.url + cacheBuster);
        }
        showSnackbar('Logo uploaded successfully', 'success');
      } else {
        throw new Error('Upload failed');
      }
    } catch (_err) {
      showSnackbar('Failed to upload logo', 'error');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleExternalClientModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnableExternalClientMode(e.target.checked);
  };

  const handleBillingVisibilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHideBillingFromUsers(e.target.checked);
  };

  const handleAiSettingChange =
    (setting: keyof typeof defaultAiSettings) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setAiSettings((prev) => ({
        ...prev,
        [setting]: e.target.checked,
      }));
    };

  const handleSave = async () => {
    try {
      const clientPayload: Record<string, unknown> = {
        enablePartnerSearch,
        enablePartnerVisibility,
        brand_identity: {
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          font_family: fontFamily,
          logo_storage_path: logoStoragePath,
        },
        clientSettings: {
          enableExternalClientMode,
          hideBillingFromUsers,
          generateCapabilityPitchDeck,
          capabilityPitchTenderContextSlideLength,
          capabilityPitchPitchSlideLength,
          capabilityPitchOutputTypes,
          presentationEngine,
          enableProcurementModule,
          enableBiddingModule,
        },
      };

      if (enablePartnerSearch) {
        clientPayload.matchmakingCountryFilter = matchmakingCountryFilter;
        clientPayload.matchmakingRegionFilter = matchmakingRegionFilter;
        clientPayload.matchmakingMinScore = matchmakingMinScore;
      }

      await updateAccountDetails({
        client: clientPayload,
        aiSettings: {
          ...account?.aiSettings,
          ...aiSettings,
        },
      });

      const partnerToggled =
        Boolean(enablePartnerSearch) !== Boolean(account?.enablePartnerSearch) ||
        Boolean(enablePartnerVisibility) !== Boolean(account?.enablePartnerVisibility);

      const moduleToggled =
        Boolean(enableProcurementModule) !==
          Boolean(account?.clientSettings?.enableProcurementModule) ||
        Boolean(enableBiddingModule) !== (account?.clientSettings?.enableBiddingModule !== false);

      if (partnerToggled || moduleToggled) {
        await refreshUserDetails();
      }

      showSnackbar('Client settings saved successfully', 'success');
    } catch (_err) {
      showSnackbar('Failed to save client settings', 'error');
    }
  };

  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  });

  const isInitialMount = useRef(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger auto-save on state change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSaveRef.current();
    }, 1500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    enableExternalClientMode,
    hideBillingFromUsers,
    generateCapabilityPitchDeck,
    capabilityPitchTenderContextSlideLength,
    capabilityPitchPitchSlideLength,
    capabilityPitchOutputTypes,
    presentationEngine,
    enableProcurementModule,
    enableBiddingModule,
    enablePartnerSearch,
    matchmakingCountryFilter,
    matchmakingRegionFilter,
    matchmakingMinScore,
    enablePartnerVisibility,
    aiSettings,
    primaryColor,
    secondaryColor,
    fontFamily,
    logoStoragePath,
  ]);

  return {
    isLegacyAdmin,
    enableExternalClientMode,
    handleExternalClientModeChange,
    hideBillingFromUsers,
    handleBillingVisibilityChange,
    generateCapabilityPitchDeck,
    setGenerateCapabilityPitchDeck,
    capabilityPitchTenderContextSlideLength,
    setCapabilityPitchTenderContextSlideLength,
    capabilityPitchPitchSlideLength,
    setCapabilityPitchPitchSlideLength,
    capabilityPitchOutputTypes,
    setCapabilityPitchOutputTypes,
    presentationEngine,
    setPresentationEngine,
    enableProcurementModule,
    setEnableProcurementModule,
    enableBiddingModule,
    setEnableBiddingModule,
    enablePartnerSearch,
    setEnablePartnerSearch,
    matchmakingCountryFilter,
    setMatchmakingCountryFilter,
    matchmakingRegionFilter,
    setMatchmakingRegionFilter,
    matchmakingMinScore,
    setMatchmakingMinScore,
    enablePartnerVisibility,
    setEnablePartnerVisibility,
    aiSettings,
    handleAiSettingChange,
    primaryColor,
    setPrimaryColor,
    secondaryColor,
    setSecondaryColor,
    fontFamily,
    setFontFamily,
    logoStoragePath,
    logoUrl,
    isUploadingLogo,
    handleLogoUpload,
  };
};
